/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';

export interface DiagnosticsHeaderProps {
  lastReportedAt?: string;
}

const useStyles = makeStyles(() => ({
  pill : {
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'center',
    flexShrink      : 0,
    padding         : '4px 8px',
    gap             : '4px',
    borderRadius    : '4px',
    backgroundColor : dlsColor('aquamarine'),
    fontFamily      : 'Inter',
    fontWeight      : 600,
    fontSize        : '12px',
    lineHeight      : '16px',
    letterSpacing   : '0.5px',
    color           : dlsColor('darkTeal')
  },
  pillText : {
    '&::first-letter' : {
      textTransform : 'uppercase'
    }
  }
}));

/**
 * DiagnosticsHeader is the ION-7462 Diagnostics accordion's own `header`
 * content: the shared `AccordionSectionHeader` title row, with a freshness
 * pill showing time since the asset's last report (e.g. "8 Hrs Ago")
 * rendered as `AccordionSectionHeader`'s `trailingContent` - immediately
 * after the title, not pushed to the far end of the accordion. Per the
 * Figma "Diagnostic Header" annotation, this header has no info icon -
 * unlike every other Asset Details Drawer section's header - so no
 * `infoTooltip` is passed to `AccordionSectionHeader`. `lastReportedAt` is
 * passed in already formatted; this component does no date math itself.
 * Omitting it (e.g. while the asset's diagnostics are still loading) omits
 * the pill entirely rather than showing a placeholder like "N/A" next to
 * the title.
 *
 * ```typescript
 *
 *   import {DiagnosticsHeader} from
 *     '@ion/dls/src/components/assetdetails/current/DiagnosticsHeader';
 *   ...
 *
 *   <DiagnosticsHeader lastReportedAt='8 Hrs Ago' />
 *
 * ```
 *
 */
export const DiagnosticsHeader = ({lastReportedAt}: DiagnosticsHeaderProps) => {
  const classes = useStyles();

  return (
    <AccordionSectionHeader title='DIAGNOSTICS'
      trailingContent={
        lastReportedAt && (
          <span className={classes.pill}>
            <span className={classes.pillText}>
              {lastReportedAt}
            </span>
          </span>
        )
      } />
  );
};

export default memo(DiagnosticsHeader);
